// Home Screen - Full-Width Responsive Layout
import { useApp } from '../hooks/useApp';
import { SCREENS, ANALYSIS_TYPES } from '../config/constants';
import { Container, Text, Card, Button, ScoreRing } from '../components/ui';
import Icons from '../components/Icons';
import BottomNav from '../components/BottomNav';
import sessionCache from '../services/sessionCache';

function HomeScreen() {
    const { setCurrentScreen, setAnalysisType, baselineData, history, resetAnalysis } = useApp();

    const latestScore = history.length > 0 ? history[0].score : null;
    const baselineScore = baselineData.established ? 82 : null;
    const scoreDiff = latestScore && baselineScore ? latestScore - baselineScore : null;

    // Check for existing session
    const hasSession = sessionCache.hasValidSession();
    const sessionSummary = hasSession ? sessionCache.getSessionSummary() : null;

    const startAnalysis = (type) => {
        setAnalysisType(type);
        setCurrentScreen(SCREENS.CAMERA);
    };

    const continueSession = () => {
        // Session data is already loaded in AppContext
        // Go directly to questionnaire or analysis
        const cachedImages = sessionCache.getCachedImages();
        if (cachedImages && Object.keys(cachedImages).length > 0) {
            setCurrentScreen(SCREENS.QUESTIONNAIRE);
        } else {
            setCurrentScreen(SCREENS.CAMERA);
        }
    };

    const startNewSession = () => {
        resetAnalysis();
        startAnalysis(ANALYSIS_TYPES.FULL);
    };

    return (
        <Container className="min-h-screen bg-theme-bg pb-24" safe>
            {/* Main Content - Centered on larger screens */}
            <div className="main-content">
                {/* Header */}
                <div className="flex items-center justify-between py-4 sm:py-6 lg:py-8">
                    <div>
                        <Text variant="small" className="mb-1">Xin chào 👋</Text>
                        <Text variant="h4" className="sm:text-2xl lg:text-3xl">Kiểm tra sức khỏe</Text>
                    </div>
                    <ScoreRing score={latestScore} size={56} strokeWidth={4} />
                </div>

                {/* Session Resume Banner */}
                {hasSession && sessionSummary && (
                    <Card className="mb-5 sm:mb-6 border-2 border-primary/30 bg-primary/5">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="text-xl">📝</span>
                            <div className="flex-1">
                                <Text className="font-semibold text-sm">Có phiên chưa hoàn thành</Text>
                                <Text variant="small" className="text-theme-tertiary">
                                    {sessionSummary.imageCount} ảnh • {sessionSummary.timeAgo}
                                </Text>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={continueSession} className="flex-1" size="sm">
                                Tiếp tục
                            </Button>
                            <Button variant="secondary" onClick={startNewSession} className="flex-1" size="sm">
                                Làm mới
                            </Button>
                        </div>
                    </Card>
                )}

                {/* Main CTA */}
                <div className="mb-5 sm:mb-6 lg:mb-8">
                    <button
                        className="w-full bg-gradient-to-r from-primary via-pink-500 to-secondary rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 text-left active:scale-[0.99] transition-transform shadow-lg touch-target"
                        onClick={() => startAnalysis(ANALYSIS_TYPES.FULL)}
                    >
                        <div className="flex items-center gap-4 sm:gap-6">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-white/25 backdrop-blur rounded-2xl flex items-center justify-center flex-shrink-0">
                                <span className="text-3xl sm:text-4xl lg:text-5xl">🔍</span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className="font-bold text-lg sm:text-xl lg:text-2xl text-white block">Kiểm tra sức khỏe</span>
                                <span className="text-sm sm:text-base text-white/80 block mt-1 sm:mt-2">Chụp ảnh mặt + lưỡi • 2 phút</span>
                            </div>
                            <div className="text-white/80 flex-shrink-0">
                                <Icons.ChevronRight />
                            </div>
                        </div>
                    </button>
                </div>

                {/* Quick Actions */}
                <div className="mb-5 sm:mb-6 lg:mb-8">
                    <Text variant="small" className="uppercase tracking-wider mb-3">Kiểm tra nhanh</Text>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <Card
                            className="cursor-pointer active:scale-[0.98] transition-transform hover:shadow-lg"
                            onClick={() => startAnalysis(ANALYSIS_TYPES.TONGUE)}
                        >
                            <div className="flex flex-col items-center text-center gap-2 py-2">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl bg-info/20 flex items-center justify-center text-2xl sm:text-3xl lg:text-4xl">
                                    👅
                                </div>
                                <div>
                                    <Text className="font-semibold text-sm sm:text-base lg:text-lg block">Kiểm tra lưỡi</Text>
                                    <Text variant="small" className="mt-0.5">1 phút</Text>
                                </div>
                            </div>
                        </Card>
                        <Card
                            className="cursor-pointer active:scale-[0.98] transition-transform hover:shadow-lg"
                            onClick={() => startAnalysis(ANALYSIS_TYPES.FACE)}
                        >
                            <div className="flex flex-col items-center text-center gap-2 py-2">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl bg-secondary/20 flex items-center justify-center text-2xl sm:text-3xl lg:text-4xl">
                                    😊
                                </div>
                                <div>
                                    <Text className="font-semibold text-sm sm:text-base lg:text-lg block">Kiểm tra mặt</Text>
                                    <Text variant="small" className="mt-0.5">1 phút</Text>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>


                {/* Tips */}
                <div className="mb-6">
                    <Card>
                        <div className="flex items-start gap-3 sm:gap-4">
                            <span className="text-xl sm:text-2xl flex-shrink-0">💡</span>
                            <div>
                                <Text className="font-semibold text-sm sm:text-base block mb-1">Mẹo kiểm tra</Text>
                                <Text variant="small" className="leading-relaxed sm:text-sm">
                                    Chụp ảnh dưới ánh sáng tự nhiên, không makeup. Thè lưỡi thoải mái, nhìn thẳng camera.
                                </Text>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <BottomNav />
        </Container>
    );
}

export default HomeScreen;
